/**
 * Waiting Config Service
 * API calls for waiting screen configuration management
 */

import { apiConfig } from '../config/api';

const BASE_URL = apiConfig.brandsApi;

export interface WaitingModules {
  reels: boolean;
  brand_reels: boolean;
  chat_offer: boolean;
  highlight: boolean;
}

export interface WaitingTriggers {
  highlight_pct: number;
  chat_pct: number;
}

export interface WaitingConfig {
  layout: 'single_stream';
  soft_gate_seconds: number;
  estimated_duration_seconds: number;
  modules: WaitingModules;
  triggers: WaitingTriggers;
}

export interface WaitingConfigResponse {
  configId: string | null;
  config: WaitingConfig;
}

export type WaitingType = 'fashplay' | 'fashsnap' | 'fashmatch';

export interface WaitingTypeOption {
  value: WaitingType;
  label: string;
}

export const WAITING_TYPES: WaitingTypeOption[] = [
  { value: 'fashplay', label: 'FashPlay' },
  { value: 'fashsnap', label: 'FashSnap' },
  { value: 'fashmatch', label: 'FashMatch' },
];

export const DEFAULT_CONFIG: WaitingConfig = {
  layout: 'single_stream',
  soft_gate_seconds: 12,
  estimated_duration_seconds: 30,
  modules: {
    reels: true,
    brand_reels: false,
    chat_offer: false,
    highlight: true,
  },
  triggers: {
    highlight_pct: 50,
    chat_pct: 70,
  },
};

export class WaitingConfigService {
  /**
   * Get waiting config for a brand and waiting type
   */
  static async getConfig(
    brandId: string,
    waitingType: WaitingType
  ): Promise<WaitingConfigResponse> {
    try {
      const response = await fetch(`${BASE_URL}/api/v1/waiting/config/${brandId}/${waitingType}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch waiting config');
      }

      if (!data.success || !data.data) {
        throw new Error('Invalid API response');
      }

      return {
        configId: data.data.configId,
        config: data.data.config,
      };
    } catch (error) {
      console.error('Failed to fetch waiting config:', error);
      return {
        configId: null,
        config: DEFAULT_CONFIG,
      };
    }
  }

  /**
   * Update waiting config for a brand and waiting type
   */
  static async updateConfig(
    brandId: string,
    waitingType: WaitingType,
    config: Partial<WaitingConfig>
  ): Promise<WaitingConfig> {
    const response = await fetch(`${BASE_URL}/api/v1/waiting/config/${brandId}/${waitingType}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(config),
    });
    
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to update waiting config');
    }

    if (!data.success || !data.data) {
      throw new Error('Invalid API response');
    }

    return data.data.config;
  }
}
